import {
  forwardRef,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useKeyboardHold } from '../state/modal-stack.ts';
import { quoteDisplayStatus, type QuoteDisplayStatus, type QuoteStatus } from '@shared/constants/index.ts';

/**
 * Form and layout primitives.
 *
 * Every control is wired to a real <label>, and validation messages are
 * associated with `aria-describedby` and announced, so errors reach screen
 * readers rather than only appearing in red (spec §27).
 */

export function classes(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Field wrapper
// ---------------------------------------------------------------------------

interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

export function Field({ label, hint, error, required, children }: FieldProps) {
  const { t } = useTranslation();
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="mb-4">
      <label className="field-label" htmlFor={id}>
        {label}
        {required ? (
          <span className="ml-1 text-[color:var(--color-danger)]" aria-hidden="true">
            *
          </span>
        ) : null}
        {required ? <span className="sr-only">{t('common.requiredSr')}</span> : null}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint ? (
        <p className="field-hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field-error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

/**
 * What a field's contents are for.
 *
 * `internal` tints the control, for the notes that stay inside the company. It
 * is deliberately a prop on the primitive rather than a class each caller
 * remembers: "internal notes" appears on five screens, and one of them
 * forgetting is exactly the sort of inconsistency that teaches people not to
 * trust the signal. The wording under the field is still what carries the
 * meaning — the color only makes it noticeable (spec §27).
 */
export type FieldTone = 'default' | 'internal';

function toneClass(tone: FieldTone | undefined): string | false {
  return tone === 'internal' && 'field-control-internal';
}

type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: ReactNode;
  error?: string;
  tone?: FieldTone;
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, hint, error, required, className, tone, ...rest },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <input
          {...rest}
          ref={ref}
          id={id}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={classes('field-control', toneClass(tone), className)}
        />
      )}
    </Field>
  );
});

type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  hint?: ReactNode;
  error?: string;
  tone?: FieldTone;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, hint, error, required, className, tone, ...rest },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <textarea
          {...rest}
          ref={ref}
          id={id}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={classes('field-control', toneClass(tone), className)}
        />
      )}
    </Field>
  );
});

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  hint?: ReactNode;
  error?: string;
  /** `disabled` shows a choice that cannot be picked right now, with its reason in the label. */
  options: ReadonlyArray<{ value: string; label: string; disabled?: boolean }>;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, required, options, className, ...rest },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <select
          {...rest}
          ref={ref}
          id={id}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={classes('field-control', className)}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
});

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  hint?: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, className, ...rest },
  ref,
) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className="mb-3 flex items-start gap-3">
      <input
        {...rest}
        ref={ref}
        id={id}
        type="checkbox"
        aria-describedby={hint ? hintId : undefined}
        className={classes('mt-1 h-5 w-5 shrink-0 rounded border-[color:var(--color-line)]', className)}
      />
      <div>
        <label htmlFor={id} className="text-base font-medium">
          {label}
        </label>
        {hint ? (
          <p className="field-hint" id={hintId}>
            {hint}
          </p>
        ) : null}
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'warning' | 'ghost';
  busy?: boolean;
}

// Forwards its ref like the field primitives above do, so a caller that has to
// put focus somewhere by hand — back onto the button a confirmation was asked
// from, most often — does not have to drop to a bare <button> and re-apply the
// classes to get a handle on it.
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', busy, children, className, disabled, ...rest },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={classes('btn', `btn-${variant}`, className)}
    >
      {busy ? <Spinner /> : null}
      {children}
    </button>
  );
});

export interface SplitButtonAction {
  key: string;
  label: string;
  /** Shown under the label, for what the choice does differently. */
  hint?: string;
  disabled?: boolean;
  onSelect: () => void;
}

/**
 * A primary button with a menu of related actions hanging off it.
 *
 * For a control with one obvious answer and a couple of variations on it: the
 * common choice stays one click away, and the alternatives do not each need a
 * button competing with it. The menu follows the APG menu-button pattern —
 * `aria-haspopup`, arrow keys between items, Escape back to the toggle — so it
 * is operable without a pointer (spec §27).
 */
export function SplitButton({
  label,
  onClick,
  actions,
  menuLabel,
  variant = 'primary',
  busy,
  disabled,
}: {
  label: string;
  onClick: () => void;
  actions: readonly SplitButtonAction[];
  /** Names the toggle for screen readers, e.g. "More ways to finish". */
  menuLabel: string;
  variant?: 'primary' | 'secondary' | 'danger';
  busy?: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  /** Which end of the menu to focus once it opens, when opened by keyboard. */
  const [pendingFocus, setPendingFocus] = useState<'first' | 'last' | null>(null);
  /**
   * Where the portaled menu goes, read from the toggle right before it opens.
   * Null (and so unrendered) until then — there is nothing to position it
   * against before the first open.
   */
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const items = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      // The menu is portaled to the body (see below), so it is no longer a
      // descendant of `wrapper` and needs its own check here.
      if (wrapper.current?.contains(target) || menu.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  /**
   * Where the menu belongs, anchored to the toggle's current position and
   * clamped to the viewport — shared by the first open, the post-render
   * correction, and every reposition while scrolling, so all three agree on
   * the same rule instead of three slightly different ones drifting apart.
   * Before the menu has ever rendered there is nothing to measure its own
   * size against, so it is left unclamped — `useLayoutEffect` below corrects
   * that the instant it has something to measure.
   */
  function positionMenu(): { top: number; left: number } | null {
    const toggleEl = toggle.current;
    if (!toggleEl) return null;
    const toggleRect = toggleEl.getBoundingClientRect();
    const margin = 8;
    let left = toggleRect.left;
    let top = toggleRect.bottom + 4;
    const menuEl = menu.current;
    if (menuEl) {
      const { width, height } = menuEl.getBoundingClientRect();
      if (left + width > window.innerWidth - margin) {
        left = Math.max(margin, window.innerWidth - margin - width);
      }
      if (top + height > window.innerHeight - margin) {
        // No room below: hang it from the toggle's top edge instead.
        top = Math.max(margin, toggleRect.top - height - 4);
      }
    }
    return { top, left };
  }

  // A portaled menu no longer scrolls with whatever it was opened from, so
  // without this it would stay pinned to where it first opened while a table
  // row — or the whole page — scrolled out from under it, ending up hanging
  // over unrelated content with no visible connection to its toggle. This
  // used to close the menu outright on any scroll instead of repositioning
  // it, which read fine for a mouse but broke exactly the interaction a
  // portal exists to support: clicking a menu item that a browser (or an
  // automated one, mid-click) scrolls into view closes the menu before the
  // click can land on it, since that scroll is itself caught here. Capture
  // phase: the table's own horizontal scrollbar is a descendant that does
  // not bubble scroll events up on its own.
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      const next = positionMenu();
      if (next)
        setMenuPosition((current) =>
          current && current.top === next.top && current.left === next.left ? current : next,
        );
    };
    document.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  // The first position `openMenu` sets is a guess — the toggle's own corner,
  // before the menu has ever been measured — and is wrong exactly when there
  // is no room where it points: this action column sits at the table's right
  // edge, so a menu wide enough to run past the viewport there is the common
  // case, not the exception. Re-measuring against the menu's actual size in a
  // `useLayoutEffect`, before the browser paints it, means the correction
  // itself is never visible as a jump.
  useLayoutEffect(() => {
    if (!open) return;
    const next = positionMenu();
    if (next)
      setMenuPosition((current) =>
        current && current.top === next.top && current.left === next.left ? current : next,
      );
  }, [open]);

  // Focus lands after the menu has rendered, so it is done here rather than in
  // the key handler that asked for it.
  useEffect(() => {
    if (!open || !pendingFocus) return;
    const rendered = items.current.filter((node): node is HTMLButtonElement => Boolean(node));
    (pendingFocus === 'first' ? rendered[0] : rendered[rendered.length - 1])?.focus();
    setPendingFocus(null);
  }, [open, pendingFocus]);

  /**
   * The menu is positioned with `fixed` coordinates rather than by sitting
   * `absolute` under its own button, precisely so a scrolling ancestor — the
   * quotes list's own horizontally-scrolling table wrapper — cannot clip it
   * off the bottom the way an un-portaled dropdown does there.
   */
  function openMenu() {
    setMenuPosition(positionMenu());
    setOpen(true);
  }

  function openWith(edge: 'first' | 'last') {
    openMenu();
    setPendingFocus(edge);
  }

  function close(returnFocus: boolean) {
    setOpen(false);
    setPendingFocus(null);
    if (returnFocus) toggle.current?.focus();
  }

  function moveFocus(from: number, delta: number) {
    const rendered = items.current.filter((node): node is HTMLButtonElement => Boolean(node));
    if (rendered.length === 0) return;
    const next = (from + delta + rendered.length) % rendered.length;
    rendered[next]?.focus();
  }

  return (
    <div className="relative inline-flex" ref={wrapper}>
      <button
        type="button"
        disabled={disabled || busy}
        aria-busy={busy || undefined}
        onClick={() => {
          // Otherwise a menu opened before this click stays up and clickable
          // through the whole action it started — `busy` disables the two
          // controls that toggle it, not the items already on screen — and a
          // second action fired from one of them races the first.
          close(false);
          onClick();
        }}
        className={classes('btn', `btn-${variant}`, 'rounded-r-none')}
      >
        {busy ? <Spinner /> : null}
        {label}
      </button>
      <button
        ref={toggle}
        type="button"
        disabled={disabled || busy}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={menuLabel}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            openWith('first');
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            openWith('last');
          } else if (event.key === 'Escape') {
            close(false);
          }
        }}
        // A lighter divider so the pair still reads as one control on a solid
        // brand fill, where two adjacent brand-colored borders vanish.
        className={classes(
          'btn',
          `btn-${variant}`,
          '-ml-px rounded-l-none px-3',
          variant === 'primary' && 'border-l-white/40',
        )}
      >
        <span aria-hidden="true" className="text-xs leading-none">
          ▾
        </span>
      </button>

      {open && menuPosition
        ? createPortal(
            <div
              ref={menu}
              role="menu"
              aria-label={menuLabel}
              // Fixed pixel coordinates, read from the toggle button in
              // `openMenu` — see that function's comment for why a portaled
              // menu cannot be positioned with `absolute` under its own
              // button here. Left-anchored and capped at the viewport, same
              // as before the portal: a right-aligned menu wider than its
              // button runs off the left of a phone screen, taking half its
              // labels with it.
              style={{ top: menuPosition.top, left: menuPosition.left }}
              className="fixed z-20 w-72 max-w-[calc(100vw-2rem)] rounded-md border border-[color:var(--color-line)] bg-white p-1 shadow-lg"
            >
              {actions.map((action, index) => (
                <button
                  key={action.key}
                  ref={(node) => {
                    items.current[index] = node;
                  }}
                  type="button"
                  role="menuitem"
                  // `aria-disabled` rather than `disabled`: a natively disabled
                  // button cannot take focus, so an unavailable first item would
                  // swallow the arrow keys and strand everything below it — and
                  // the hint saying *why* it is unavailable would never be read.
                  // `busy` too: the primary button closes the menu the instant
                  // it starts one of these, but a menu item can only be reached
                  // at all while this one is rendered, so this is what stops a
                  // click that lands in the gap before that close takes effect.
                  aria-disabled={action.disabled || busy || undefined}
                  onClick={() => {
                    if (action.disabled || busy) return;
                    close(true);
                    action.onSelect();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowDown') {
                      event.preventDefault();
                      moveFocus(index, 1);
                    } else if (event.key === 'ArrowUp') {
                      event.preventDefault();
                      moveFocus(index, -1);
                    } else if (event.key === 'Escape') {
                      close(true);
                    } else if (event.key === 'Tab') {
                      close(false);
                    }
                  }}
                  className={classes(
                    'block w-full rounded px-3 py-2 text-left text-sm',
                    action.disabled || busy ? 'cursor-not-allowed opacity-60' : 'hover:bg-[color:var(--color-surface)]',
                  )}
                >
                  <span className="font-semibold">{action.label}</span>
                  {action.hint ? <span className="mt-0.5 block field-hint">{action.hint}</span> : null}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

// ---------------------------------------------------------------------------
// Confirmation
// ---------------------------------------------------------------------------

/**
 * A modal that asks before something happens, rendered only while open.
 *
 * `window.confirm` is what the rest of the app reaches for, and it is fine for
 * "are you sure" — but it can only carry one line of unstyled text and it
 * blocks the tab. Where the answer depends on facts the reader needs in front
 * of them (which quote, whose, how much, and what changes as a side effect),
 * those facts have to be *in* the question, and the question has to be able to
 * show a spinner and an error where the button is rather than after a second
 * dialog.
 *
 * Keyboard behavior follows the APG dialog pattern (spec §27): focus moves to
 * the confirm button on open and is trapped in the dialog, Escape and the
 * backdrop both cancel, and focus returns to whatever opened it on close —
 * which is why callers need no ref of their own.
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel,
  confirmVariant = 'primary',
  confirmDisabled,
  initialFocus = 'confirm',
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  title: string;
  /** The facts the answer depends on. */
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  confirmVariant?: 'primary' | 'danger';
  /**
   * For a confirmation whose own fields are not yet in a state that can be
   * acted on — a sub-choice still open, a lookup the answer depends on still
   * in flight. Distinct from `busy`, which is the action already running:
   * this leaves the rest of the dialog live so the reader can finish
   * answering it.
   */
  confirmDisabled?: boolean;
  /** Destructive questions can start on the safe answer. */
  initialFocus?: 'confirm' | 'cancel';
  busy?: boolean;
  /** Shown inside the dialog, so a failure does not close the question. */
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const confirmButton = useRef<HTMLButtonElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);

  // Declared here rather than at each of the dozen call sites: a confirmation
  // is modal wherever it was raised from, and the trap below only holds Tab —
  // a shortcut listening on `window` hears the keystroke regardless. See
  // state/modal-stack.ts.
  useKeyboardHold();

  useEffect(() => {
    const opener = document.activeElement;
    const dialog = panel.current;
    // Read a tall question from its title instead of scrolling to its answer.
    // Short confirmations retain their existing initial confirm-button focus.
    const initial =
      dialog &&
      (dialog.scrollHeight > dialog.clientHeight || (initialFocus === 'confirm' && confirmButton.current?.disabled))
        ? dialog.querySelector<HTMLHeadingElement>('h2')
        : initialFocus === 'cancel'
          ? cancelButton.current
          : confirmButton.current;
    initial?.focus({ preventScroll: true });
    return () => {
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, []);

  function onKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      // Not while the answer is being acted on. Escape cannot recall a request
      // already sent, so honoring it here would close the dialog on somebody
      // who believes they stopped something that is still going to happen.
      if (!busy) onCancel();
      return;
    }
    if (event.key !== 'Tab') return;
    // Only enabled, interactive controls. Some confirmations collect a
    // reason or attribution before they run, so their fields belong in the
    // same keyboard loop as the action buttons.
    const focusable = [
      ...(panel.current?.querySelectorAll<HTMLElement>(
        [
          'button:not([disabled])',
          'a[href]',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])',
        ].join(','),
      ) ?? []),
    ];
    // Nothing left to move to — every control is disabled while busy — so Tab
    // holds rather than walking focus out of a dialog that is still up.
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    // Tall dialogs initially focus the heading; disabled controls may also
    // retain focus after a request. Neither is a stop in the interactive loop.
    if (!focusable.some((element) => element === active)) {
      event.preventDefault();
      (event.shiftKey ? last : first)?.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  /*
   * Mounted on the body rather than where it was written.
   *
   * A dialog is opened from wherever its question comes up, and some of those
   * places are cells in a table — a row's action column keeps its buttons on
   * one line, and a totals column aligns its text right. `position: fixed`
   * takes the dialog out of that layout but not out of the document, and text
   * properties inherit down the tree it is still written in: the same prose
   * that reads normally in a form came out of a list row as a single
   * right-aligned line running off the panel and off the screen.
   *
   * So the question is asked from the top of the document, where nothing but
   * the page's own defaults reaches it, and it reads the same wherever it was
   * raised from. The portal is a DOM move only — React events still travel the
   * tree this component sits in, so the trap and the backdrop below behave as
   * they read.
   */
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[color:var(--color-ink)]/50 p-4 text-start whitespace-normal sm:items-center"
      // A click that starts inside the panel and ends on the backdrop (a
      // dragged selection) is not a click on the backdrop, so the target is
      // checked against the element the listener sits on. Ignored while busy,
      // for the same reason Escape is.
      onMouseDown={(event) => {
        if (!busy && event.target === event.currentTarget) onCancel();
      }}
      onKeyDown={onKeyDown}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // `break-words` for the parts written by other people: a customer name
        // or an address with no spaces in it is still a name, and it has to
        // fold rather than push the panel out from the inside.
        className="max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-lg border border-[color:var(--color-line)] bg-white p-5 break-words shadow-xl"
      >
        <h2 id={titleId} tabIndex={-1} className="text-lg font-semibold">
          {title}
        </h2>
        {children ? <div className="mt-3 text-[color:var(--color-ink)]">{children}</div> : null}
        {error ? (
          <div className="mt-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}
        {/* Reversed on a phone so the confirm sits under the thumb, and the
            cancel — the one nobody wants to hit by accident — does not. */}
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button ref={cancelButton} onClick={onCancel} disabled={busy}>
            {cancelLabel ?? t('common.cancel')}
          </Button>
          <Button
            ref={confirmButton}
            variant={confirmVariant}
            busy={busy}
            disabled={confirmDisabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export function Alert({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'success' | 'error' | 'warning';
  title?: string;
  children: ReactNode;
}) {
  const toneClasses: Record<string, string> = {
    info: 'border-l-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] border-[color:var(--color-line)]',
    success: 'border-l-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] border-[color:var(--color-line)]',
    error: 'border-l-[color:var(--color-danger)] bg-[color:var(--color-danger-soft)] border-[color:var(--color-line)]',
    warning:
      'border-l-[color:var(--color-warning)] bg-[color:var(--color-warning-soft)] border-[color:var(--color-line)]',
  };

  return (
    <div className={classes('alert', toneClasses[tone])} role={tone === 'error' ? 'alert' : 'status'}>
      {title ? <p className="mb-1 font-semibold">{title}</p> : null}
      <div className="text-sm">{children}</div>
    </div>
  );
}

const STATUS_TONE: Record<QuoteDisplayStatus, string> = {
  draft: 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
  ready: 'border-[color:var(--color-brand)] text-[color:var(--color-brand)]',
  sent: 'border-[color:var(--color-brand)] text-[color:var(--color-brand)]',
  viewed: 'border-[color:var(--color-brand)] text-[color:var(--color-brand)]',
  accepted:
    'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]',
  declined: 'border-[color:var(--color-danger)] text-[color:var(--color-danger)]',
  expired: 'border-[color:var(--color-warning)] text-[color:var(--color-warning)]',
  completed:
    'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]',
  // Booked. Filled like accepted, because it IS accepted and then some — the
  // reader should not have to work out that the further-along state is the
  // one drawn more quietly.
  scheduled:
    'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]',
  cancelled: 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
};

/**
 * What a quote is, in one word.
 *
 * Two of the words are derived rather than stored — `expired` from the date,
 * `scheduled` from the booking that claimed the quote — so callers hand over
 * the whole picture and `quoteDisplayStatus` decides, rather than each page
 * working out its own precedence and one of them getting it wrong.
 */
export function StatusBadge({
  status,
  expired,
  quote,
}: {
  status: QuoteStatus;
  expired?: boolean;
  /**
   * The booking counters, when the caller has them. A quote is "Scheduled"
   * once it has a schedule. That one schedule carries every weekday the
   * contract visits.
   */
  quote?: { scheduledSeriesCount?: number; visitsPerWeek?: number | null };
}) {
  const { t } = useTranslation();
  const effective = quoteDisplayStatus({ status, isExpired: expired, ...quote });
  return (
    // The label carries the meaning; color only reinforces it (spec §27).
    <span className={classes('badge', STATUS_TONE[effective])}>{t(`quotes.status.${effective}`)}</span>
  );
}

export function EmptyState({
  title,
  children,
  className,
}: {
  title: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={classes('empty-state', className)}>
      <svg aria-hidden="true" viewBox="0 0 64 64" className="empty-state-art">
        <rect x="8" y="12" width="44" height="40" rx="12" fill="currentColor" opacity=".09" />
        <path
          d="M22 34h20M22 41h12M22 27h12M46 7v10M41 12h10"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
      <p className="text-lg font-semibold">{title}</p>
      {children ? <div className="mt-2 text-[color:var(--color-muted)]">{children}</div> : null}
    </div>
  );
}

/**
 * Previous/next controls for a paged list.
 *
 * Renders nothing when there is only one page, so a caller can drop it under
 * any list without first deciding whether that list is long enough to need it.
 * `page`/`totalPages` come from the list envelope the API returns, which is why
 * every paged endpoint carries them (see the Worker's `page` helper).
 */
export function Pagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  /**
   * Pass the query's own value, and `undefined` while it has not answered —
   * never a fallback like `?? 1`. The difference is load-bearing: see below.
   */
  totalPages: number | undefined;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation();

  // A page that has stopped existing — deletions shrank the list while someone
  // was looking at the end of it — is walked back here rather than at each call
  // site. Without this the caller sits on an empty page with no controls to
  // leave it, because a one-page list renders no pager at all.
  //
  // Only when the total is actually known, though. "Page 2, total unknown" is
  // what every caller looks like for the moment after Next is clicked and
  // before that page's query answers, and treating that as a vanished page
  // would bounce the reader straight back to page 1 — making Next unable to
  // reach anything not already cached.
  useEffect(() => {
    if (totalPages !== undefined && page > totalPages) onPageChange(totalPages);
  }, [page, totalPages, onPageChange]);

  if (totalPages === undefined || totalPages <= 1) return null;

  return (
    <nav className="mt-4 flex items-center justify-between" aria-label={t('common.paginationAriaLabel')}>
      <Button disabled={page <= 1} onClick={() => onPageChange(Math.max(1, page - 1))}>
        {t('common.previous')}
      </Button>
      <span className="text-sm text-[color:var(--color-muted)]" aria-live="polite">
        {t('common.pageOf', { page, totalPages })}
      </span>
      <Button disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
        {t('common.next')}
      </Button>
    </nav>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  /** A node rather than a string, so a page whose subtitle is an address can
      make it the link that opens the map app. */
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0 flex-[1_1_16rem]">
        <h1 className="text-2xl font-bold">{title}</h1>
        {description ? <p className="mt-1 text-[color:var(--color-muted)]">{description}</p> : null}
      </div>
      {actions ? <div className="flex max-w-full flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export function LoadingBlock({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 p-10 text-[color:var(--color-muted)]" role="status">
      <Spinner />
      <span>{label ?? t('common.loading')}</span>
    </div>
  );
}
